import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiAlertCircle, FiCalendar, FiChevronLeft, FiChevronRight, FiPlus, FiX } from 'react-icons/fi';
import {
  clientPortalService,
  type ClientPortalContext,
  type ClientPortalCoverageSlot,
  type ClientPortalScope,
  type ClientServiceRequest,
  type ServiceRequestType,
  type ServiceRequestUrgency,
} from '../../services/clientPortalService';
import { useI18n } from '../../i18n';
import { addDaysIso, formatDate, formatDateTime, formatSlotMinute, todayIso } from './clientPortalFormat';
import './ClientPortal.css';

interface Props {
  context: ClientPortalContext;
  siteId: string | null;
  can: (scope: ClientPortalScope) => boolean;
}

const REQUEST_TYPES: Array<{ value: ServiceRequestType; label: string }> = [
  { value: 'extra_hours', label: 'Extra guard hours' },
  { value: 'emergency_dispatch', label: 'Emergency dispatch' },
  { value: 'event_coverage', label: 'Event coverage' },
  { value: 'site_visit', label: 'Supervisor site visit' },
  { value: 'other', label: 'Something else' },
];

const URGENCIES: Array<{ value: ServiceRequestUrgency; label: string }> = [
  { value: 'routine', label: 'Routine' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'emergency', label: 'Emergency' },
];

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  acknowledged: 'Acknowledged',
  scheduled: 'Scheduled',
  declined: 'Declined',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

function startOfWeek(dateKey: string): string {
  const date = new Date(`${dateKey}T00:00:00Z`);
  const day = (date.getUTCDay() + 6) % 7; // Monday-first
  date.setUTCDate(date.getUTCDate() - day);
  return date.toISOString().slice(0, 10);
}

export default function ClientSchedulePage({ context, siteId, can }: Props) {
  const { t } = useI18n();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(todayIso()));
  const [slots, setSlots] = useState<ClientPortalCoverageSlot[]>([]);
  const [requests, setRequests] = useState<ClientServiceRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const [form, setForm] = useState({
    title: '',
    requestType: 'extra_hours' as ServiceRequestType,
    urgency: 'routine' as ServiceRequestUrgency,
    siteId: siteId ?? '',
    details: '',
    start: '',
    end: '',
    guards: 1,
  });

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDaysIso(weekStart, index)),
    [weekStart],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [coverage, requestRows] = await Promise.all([
        can('schedule')
          ? clientPortalService.fetchCoverage(weekStart, addDaysIso(weekStart, 6), siteId)
          : Promise.resolve([]),
        can('service_requests') ? clientPortalService.fetchServiceRequests() : Promise.resolve([]),
      ]);
      setSlots(coverage);
      setRequests(requestRows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [can, weekStart, siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setForm((prev) => ({ ...prev, siteId: siteId ?? '' }));
  }, [siteId]);

  const slotsByDay = useMemo(() => {
    const map = new Map<string, ClientPortalCoverageSlot[]>();
    slots.forEach((slot) => {
      const bucket = map.get(slot.slotDate) ?? [];
      bucket.push(slot);
      map.set(slot.slotDate, bucket);
    });
    return map;
  }, [slots]);

  const submitRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError(t('Please describe what you need.'));
      return;
    }
    setIsBusy(true);
    setError('');
    try {
      await clientPortalService.createServiceRequest({
        title: form.title.trim(),
        requestType: form.requestType,
        urgency: form.urgency,
        siteId: form.siteId || null,
        details: form.details.trim() || null,
        requestedStartAt: form.start ? new Date(form.start).toISOString() : null,
        requestedEndAt: form.end ? new Date(form.end).toISOString() : null,
        guardsRequired: Math.max(1, Number(form.guards) || 1),
      });
      setToast(t('Request sent to your security provider.'));
      setIsFormOpen(false);
      setForm((prev) => ({ ...prev, title: '', details: '', start: '', end: '', guards: 1 }));
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  const cancelRequest = async (requestId: string) => {
    setIsBusy(true);
    try {
      await clientPortalService.cancelServiceRequest(requestId);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(''), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  return (
    <div className="cp-page">
      {error ? <div className="cp-banner cp-banner--error">{error}</div> : null}
      {toast ? <div className="cp-banner cp-banner--success">{toast}</div> : null}

      {can('schedule') ? (
        <article className="cp-panel">
          <header className="cp-panel__head">
            <div>
              <h2>{t('Coverage calendar')}</h2>
              <p>{t('Published duties confirming the cover booked for your sites.')}</p>
            </div>
            <div className="cp-week-nav">
              <button
                type="button"
                className="cp-icon-btn"
                aria-label={t('Previous week')}
                onClick={() => setWeekStart(addDaysIso(weekStart, -7))}
              >
                <FiChevronLeft aria-hidden="true" />
              </button>
              <span>
                {formatDate(weekStart)} – {formatDate(addDaysIso(weekStart, 6))}
              </span>
              <button
                type="button"
                className="cp-icon-btn"
                aria-label={t('Next week')}
                onClick={() => setWeekStart(addDaysIso(weekStart, 7))}
              >
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>
          </header>

          {isLoading ? (
            <div className="cp-loading"><div className="spinner" /></div>
          ) : (
            <div className="cp-week">
              {weekDays.map((day) => {
                const daySlots = slotsByDay.get(day) ?? [];
                const isToday = day === todayIso();
                return (
                  <div key={day} className={`cp-week__day${isToday ? ' cp-week__day--today' : ''}`}>
                    <header>
                      <span>{new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short' })}</span>
                      <strong>{new Date(`${day}T00:00:00`).getDate()}</strong>
                    </header>
                    {daySlots.length === 0 ? (
                      <p className="cp-week__empty">{t('No cover booked')}</p>
                    ) : (
                      daySlots.map((slot) => {
                        const shortfall = slot.guardsAssigned < slot.guardsRequired;
                        return (
                          <div
                            key={slot.dutySlotId}
                            className={`cp-slot${shortfall ? ' cp-slot--short' : ''}`}
                          >
                            <strong>{slot.name}</strong>
                            <span>
                              {formatSlotMinute(slot.startMinute)}–{formatSlotMinute(slot.endMinute)}
                            </span>
                            {!siteId ? <span className="cp-slot__site">{slot.siteName}</span> : null}
                            <span className={shortfall ? 'cp-slot__count cp-slot__count--short' : 'cp-slot__count'}>
                              {t('{assigned}/{required} booked', {
                                assigned: slot.guardsAssigned,
                                required: slot.guardsRequired,
                              })}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </article>
      ) : null}

      {can('service_requests') ? (
        <article className="cp-panel">
          <header className="cp-panel__head">
            <div>
              <h2>{t('Cover requests')}</h2>
              <p>{t('Ask for extra hours, emergency dispatch or event coverage.')}</p>
            </div>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => setIsFormOpen((open) => !open)}
            >
              {isFormOpen ? <FiX aria-hidden="true" /> : <FiPlus aria-hidden="true" />}
              {isFormOpen ? t('Cancel') : t('New request')}
            </button>
          </header>

          {isFormOpen ? (
            <form className="cp-form" onSubmit={submitRequest}>
              <label className="cp-field cp-field--wide">
                <span>{t('What do you need?')}</span>
                <input
                  type="text"
                  className="form__input"
                  required
                  maxLength={140}
                  value={form.title}
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                  placeholder={t('e.g. Extra officer for contractor access')}
                />
              </label>

              <label className="cp-field">
                <span>{t('Request type')}</span>
                <select
                  className="form__select"
                  value={form.requestType}
                  onChange={(event) => setForm({ ...form, requestType: event.target.value as ServiceRequestType })}
                >
                  {REQUEST_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>{t(item.label)}</option>
                  ))}
                </select>
              </label>

              <label className="cp-field">
                <span>{t('Urgency')}</span>
                <select
                  className="form__select"
                  value={form.urgency}
                  onChange={(event) => setForm({ ...form, urgency: event.target.value as ServiceRequestUrgency })}
                >
                  {URGENCIES.map((item) => (
                    <option key={item.value} value={item.value}>{t(item.label)}</option>
                  ))}
                </select>
              </label>

              <label className="cp-field">
                <span>{t('Site')}</span>
                <select
                  className="form__select"
                  value={form.siteId}
                  onChange={(event) => setForm({ ...form, siteId: event.target.value })}
                >
                  <option value="">{t('Not site specific')}</option>
                  {context.sites.map((site) => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
                </select>
              </label>

              <label className="cp-field">
                <span>{t('Officers needed')}</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  className="form__input"
                  value={form.guards}
                  onChange={(event) => setForm({ ...form, guards: Number(event.target.value) })}
                />
              </label>

              <label className="cp-field">
                <span>{t('From')}</span>
                <input
                  type="datetime-local"
                  className="form__input"
                  value={form.start}
                  onChange={(event) => setForm({ ...form, start: event.target.value })}
                />
              </label>

              <label className="cp-field">
                <span>{t('To')}</span>
                <input
                  type="datetime-local"
                  className="form__input"
                  value={form.end}
                  onChange={(event) => setForm({ ...form, end: event.target.value })}
                />
              </label>

              <label className="cp-field cp-field--wide">
                <span>{t('Extra detail')}</span>
                <textarea
                  className="form__input"
                  rows={3}
                  value={form.details}
                  onChange={(event) => setForm({ ...form, details: event.target.value })}
                  placeholder={t('Access arrangements, contacts, anything the officer should know.')}
                />
              </label>

              <div className="cp-form__actions">
                <button type="submit" className="btn btn--primary" disabled={isBusy}>
                  {isBusy ? t('Sending…') : t('Send request')}
                </button>
              </div>
            </form>
          ) : null}

          {requests.length === 0 ? (
            <div className="cp-empty">{t('You have not raised any cover requests yet.')}</div>
          ) : (
            <ul className="cp-list">
              {requests.map((request) => (
                <li key={request.id} className="cp-list__row cp-list__row--stacked">
                  <div>
                    <strong>{request.title}</strong>
                    <span>
                      {t(REQUEST_TYPES.find((item) => item.value === request.request_type)?.label ?? 'Request')}
                      {request.requested_start_at ? ` · ${formatDateTime(request.requested_start_at)}` : ''}
                      {` · ${t('{count} officers', { count: request.guards_required })}`}
                    </span>
                    {request.details ? <p className="cp-muted">{request.details}</p> : null}
                    {request.response_note ? (
                      <p className="cp-response">
                        <strong>{t('Provider response')}:</strong> {request.response_note}
                      </p>
                    ) : null}
                  </div>
                  <div className="cp-list__meta">
                    <span className={`cp-chip cp-chip--${request.status}`}>
                      {t(STATUS_LABELS[request.status] ?? request.status)}
                    </span>
                    {request.urgency !== 'routine' ? (
                      <span className="cp-chip cp-chip--warn">{t(request.urgency === 'emergency' ? 'Emergency' : 'Urgent')}</span>
                    ) : null}
                    <span>{formatDateTime(request.created_at)}</span>
                    {request.status === 'submitted' || request.status === 'acknowledged' ? (
                      <button
                        type="button"
                        className="cp-link"
                        disabled={isBusy}
                        onClick={() => void cancelRequest(request.id)}
                      >
                        {t('Withdraw')}
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>
      ) : null}

      {!can('schedule') && !can('service_requests') ? (
        <div className="cp-notice">
          <FiCalendar aria-hidden="true" />
          <div>
            <strong>{t('Scheduling is not enabled')}</strong>
            <p>{t('Your security provider controls which sections reach this portal.')}</p>
          </div>
        </div>
      ) : null}

      {can('schedule') && slots.length === 0 && !isLoading ? (
        <p className="cp-footnote">
          <FiAlertCircle aria-hidden="true" />{' '}
          {t('Only published duties appear here. Draft rosters stay hidden until your provider publishes them.')}
        </p>
      ) : null}
    </div>
  );
}
